import { Grid } from '@material-ui/core'
import { Form, FormSpy } from 'react-final-form'
import useTranslate from '../../../../../../i18n/useTranslate'
import useSynthesisReview from './useSynthesisReview'
import MyAccordion from '../../../../../../control/accordion/accordion'
import MyTextfield from '../../../../../../control/textfield/textfield'
import PageAction from '../../pageAction'
import Dialog from '../../../../../../control/dialog/dialog'
import RichTextEditor from '../../../../../../control/richTextEditor/richTextEditor'
import DisplayUpdater from '../../../../shareUI/displayUpdater/displayUpdater'
import { useEffect, useRef } from 'react'

type SynthesisReviewViewType = ReturnType<typeof useSynthesisReview>
const SynthesisReviewView = (props: SynthesisReviewViewType) => {
    const {
        formRef,
        configDialog,
        isViewMode,
        initialValues,
        isShowUpdateDate,
        initialRichTextEditor,
        onRefDialog,
        onSubmitForm,
        onDirtyChange,
        onClickBackToSearch,
        onAutoSave,
    } = props
    const translate = useTranslate()

    // ---------- autosave ----------
    // เดิมมี 2 ตัวทำงานพร้อมกัน
    //   1) setInterval ทุก 5 นาที ซึ่งไม่ได้เช็ค lastAutoSaveTime เลย
    //   2) debounce 1 นาที ตอนฟอร์มเปลี่ยน
    // อาจจะยิงซ้อนกันได้ และไม่มีอะไรกันไม่ให้ยิงรอบใหม่ทั้งที่รอบก่อนยังไม่เสร็จ
    // รวมให้เหลือทางเข้าเดียวคือ runAutoSave
    const autoSaveTimer = useRef<any>(null)
    const lastAutoSaveTime = useRef<number>(0)
    const mountTime = useRef(Date.now())
    const isAutoSavingRef = useRef(false)

    const runAutoSave = async (values: any) => {
        if (!values) return
        // กันยิงซ้อนตอนรอบก่อนหน้ายังไม่เสร็จ
        if (isAutoSavingRef.current) return

        const now = Date.now()
        // ไม่ autosave ใน 1 นาทีแรกหลังเปิดหน้า
        if (now - mountTime.current < 60 * 1000) return
        // เว้นระยะอย่างน้อย 1 นาทีจากรอบที่แล้ว
        if (now - lastAutoSaveTime.current < 60 * 1000) return

        isAutoSavingRef.current = true
        lastAutoSaveTime.current = now
        try {
            await onAutoSave(values)
        } finally {
            isAutoSavingRef.current = false
        }
    }

    useEffect(() => {
        mountTime.current = Date.now()
        lastAutoSaveTime.current = 0

        return () => {
            if (autoSaveTimer.current) {
                clearTimeout(autoSaveTimer.current)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleAutoSave = (values: any) => {
        if (!values) return

        if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current)
        }

        autoSaveTimer.current = setTimeout(() => {
            const latestValues = formRef.current?.getState?.()?.values ?? values
            runAutoSave(latestValues)
        }, 60 * 1000)
    }

    return (
        <Form onSubmit={onSubmitForm} initialValues={{ ...initialValues }} keepDirtyOnReinitialize={true}>
            {(props) => {
                formRef.current = props.form
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12} lg={12}>
                            <MyAccordion title={translate.get('LABEL.ETHIC_REVIEW_OUTCOME')}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={12} lg={12}>
                                        <MyTextfield
                                            name={`synthesisList`}
                                            materialInputProps={{
                                                disabled: true,
                                                label: translate.get('LABEL.SUGGESTION_NOTICE'),
                                                size: 'small',
                                                rowsMax: 'true',
                                                multiline: true,
                                            }}
                                            rowsMin={5}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12} lg={12}>
                                        <RichTextEditor
                                            name={`consideration`}
                                            initialRichTextEditor={initialRichTextEditor}
                                            disabled={isViewMode}
                                            isHideToolbar={isViewMode}
                                            label={translate.get('LABEL.ETHIC_REVIEW_OUTCOME')}
                                        />
                                    </Grid>
                                </Grid>
                            </MyAccordion>
                        </Grid>

                        {isShowUpdateDate && <DisplayUpdater />}

                        {!isViewMode && (
                            <Grid item xs={12} md={12} lg={12}>
                                <PageAction
                                    onClickSaveDraft={() => {
                                        props.form.change('submitType', 'draft')
                                        props.handleSubmit()
                                    }}
                                    onClickSave={() => {
                                        props.form.change('submitType', 'save')
                                        props.handleSubmit()
                                    }}
                                    onClickClose={() => {
                                        onClickBackToSearch(props.dirty)
                                    }}
                                />
                            </Grid>
                        )}
                        <FormSpy subscription={{ dirty: true, values: true }} onChange={(state) => {
                                                                                        onDirtyChange(state)
                                                                                        handleAutoSave(state.values)
                                                                                    }} />
                        <Dialog onRef={onRefDialog} {...configDialog} />
                    </Grid>
                )
            }}
        </Form>
    )
}

export type SynthesisReviewType = {
    onSaveComplete?: (resStatus: number, isDraft: boolean) => void
    projectData: any
}
const SynthesisReview = (props: SynthesisReviewType) => {
    const synthesis = useSynthesisReview(props)
    return <SynthesisReviewView {...synthesis} />
}
export default SynthesisReview
